/* eslint-disable camelcase */
import StarsBanner from "~/ui/stars-banner";
import Dialog from "~/ui/dialog";
import { MAX_LEN, RULES_LINKS, RULES_LINKS_NOFIRM, REMOVE_REASONS_LIST, REVIEW_THANK_YOU_TO, FULLTEXT_REVIEW, FULLTEXT_REVIEW_SOURCE } from "./const";
import FirmStarsReview from "./stars-review";
import { putToCacheWithoutDetail } from "~/util/reviews-cache";
import { store } from "~/state/store";
import { node, icon } from "~/util/dom";
import { sklikReviewSend } from "~/util/sklik";
import { CONFIG } from "~/config";
import { getFirmLayout, getStructuredRating, getUserName, parseStructuredRating, createDeleteInfo } from "~/util/review";
import { IReviewItem, IReview, TStructuredData } from "./interfaces";
import { IImage, createReviewPhotosUpload } from "~/ui/review/photosupload";
import { createVerifiedUserTooltip } from "~/util/userweb";
import { createEmojis } from "~/ui/review/emojis";
import FirmReviewStarsPicker from "./stars-picker";
import { putReview, loginWithDetail, deleteReview } from "~/util/providers/review";
import { IReviewConf } from "~/interfaces";
import { _ } from "~/i18n";
import { SMALL_MEDIA_QUERY } from "~/report/const";
import { ARROW_LEFT_24 } from "@sznds/icons";
import createSDSIcon from "~/ui/icon";

interface IFirmReviewPopup {
	data: IReviewConf;
	logSource?: string;
	logPosition?: string;
	textReview?: boolean;
	showThankYou?: boolean;
	onClose?: (data: { review: IReview; myReview: IReviewItem; wasRemoved: boolean }) => void;
	onBack?: (myReview: IReviewItem, images?: Array<IImage>) => void;
	logObj?: object;
	activeRequest?: boolean;
	fromOtherPOI?: boolean;
	forceRating?: number;
	initialText?: string;
	initialImages?: Array<IImage>;
}

// vlastni popup s hodnocenim - zadavani pres hvezdicky, textova recenze
export default class FirmReviewPopup {
	_opts: IFirmReviewPopup;

	_dom: {
		[key: string]: HTMLElement;
		text?: HTMLTextAreaElement;
	};

	_dialog: Dialog;

	_starsReview: FirmStarsReview;

	_activeRequest: boolean;

	_activeEl: null | HTMLElement;

	_myReview: IReviewItem;

	_detailReview: IReview;

	_locked: boolean;

	_showThankYou: boolean;

	_logOrigin: string;

	_closeLogs: Array<object>;

	_keyInputAnalytics: boolean;

	_logRecenzeLeti: boolean;

	_wasRemoved: boolean;

	_reviewPhotosUpload: ReturnType<typeof createReviewPhotosUpload>;

	_emojis: ReturnType<typeof createEmojis>;

	_addPhotoWasSended: boolean;

	_starsUpdated: boolean;

	_emojisUpdated: boolean;

	constructor(optsArg: IFirmReviewPopup) {
		const opts: IFirmReviewPopup = {
			data: null,
			logSource: "recenze-poi",
			logPosition: "bottom",
			textReview: false, // otevrit zrovna textovou editaci
			showThankYou: true, // pokud je to false, tak bez ohledu na to, jak dopadnou pravidla pro _showThankYou, tak se neukaze overlay dekovacka
			logObj: {}, // pridavna analytika v objektu
			activeRequest: false,
			fromOtherPOI: false,
			forceRating: 0,
			initialText: "",
			initialImages: [],
			...optsArg,
		};

		this._opts = opts;
		this._dialog = null;
		this._starsReview = null;
		this._dom = {
			container: null,
		};
		this._activeRequest = this._opts.activeRequest || false;
		this._activeEl = null;
		this._myReview = this._opts.data.myReview || null;
		this._detailReview = this._opts.data.review;
		this._locked = false;
		this._showThankYou = !!this._myReview;
		this._logOrigin = "step0";
		this._closeLogs = [];
		this._keyInputAnalytics = false;
		this._logRecenzeLeti = false;
		this._wasRemoved = false;
		this._reviewPhotosUpload = null;
		this._emojis = null;
		this._addPhotoWasSended = false;
		this._starsUpdated = false;
		this._emojisUpdated = false;

		this._build();
		// start s contentem
		if (this._opts.textReview && this._opts.forceRating <= 0) {
			// analytika
			this._logRecenzeLeti = true;

			this._showTextReview();
		} else {
			this._showStarsReview();
		}
	}

	destroy() {
		this._dialog?.destroy();
		this._reviewPhotosUpload?.destroy();
	}

	_build() {
		// dialog
		this._dialog = new Dialog({
			className: "firm-review-popup",
			title: "",
			onClose: () => {
				this._dialogOnClose();
			},
		});

		this._dom.container = this._dialog.dom.content;
		this._dom.starsReview = node("div", { className: "stars-review" });
		this._dom.textReview = node("div", { className: "text-review" });
		document.body.style.overflow = "hidden";

		this._buildStarsReview();
		this._dialog.open();
	}

	_buildStarsReview() {
		// stars review
		const titleCont = this._getUserTitle();
		const middleCont = node("div", { className: "middle-cont" });
		const cont = this._dom.starsReview;
		const emojisConfig = this._getEmojisConfig();

		if (emojisConfig.length) {
			const isSmallViewport = window.innerWidth < 500;
			let wasStarsClick = this._opts.forceRating > 0;
			let starsPicker = null;
			const bottomPartData = this._buildBottomPart({
				onSave: () => {
					this._saveFromStars(starsPicker.percent, this._emojis.getData());

					if (this._logOrigin === "step1Edit") {
						this._log("reviews_edit_stars", {
							starsEdit: this._starsUpdated ? 1 : 0,
							attributesEdit: this._emojisUpdated ? 1 : 0,
						});
					}
				},
				saveTitle: _("continue"),
			});

			this._emojis = createEmojis({
				config: emojisConfig,
				onUpdate: stars => {
					if (!wasStarsClick) {
						starsPicker.stars = stars;
					}

					this._emojisUpdated = true;
					bottomPartData.saveBtn.disabled = false;
				},
				// stredni hodnota SH z BE - zatim chodi pro nefiremni
				...typeof this._opts.data.review.target_stars === "number"
					? {
						SH: this._opts.data.review.target_stars,
					}
					: {},
			});
			starsPicker = new FirmReviewStarsPicker({
				starWidth: isSmallViewport ? 40 : 48,
				starOffset: isSmallViewport ? 10 : 13,
				noTitle: true,
				clickCb: starsData => {
					wasStarsClick = true;
					bottomPartData.saveBtn.disabled = false;
					// analytika hvezd
					const starsCount = ((starsData.stars - 1) * 2) + (starsData.full ? 2 : 1);

					this._log("click", {
						text: `stars-updated-${starsCount}`,
					});

					this._starsUpdated = true;
				},
			});

			if (this._opts.forceRating > 0) {
				starsPicker.percent = this._opts.forceRating;
			} else {
				bottomPartData.saveBtn.disabled = true;
			}

			middleCont.append(
				starsPicker.container,
				this._emojis.container,
			);
			cont.append(
				titleCont,
				middleCont,
				bottomPartData.container,
			);
			cont.classList.add("variant-b");

			Object.assign(this._dom, {
				saveBtn: bottomPartData.saveBtn,
				errorInfo: bottomPartData.errorInfo,
			});

			// analytika
			this._log("attributes_dialogue_show", {
				attributesCount: emojisConfig.length,
			});
		} else {
			const starsReview = this._createStarsReview();
			const bottomPart = node("div", { className: "bottom-part" });

			middleCont.append(starsReview.container);
			bottomPart.append(starsReview.agreement);

			this._starsReview = starsReview;

			cont.append(
				titleCont,
				middleCont,
				bottomPart,
			);
		}
	}

	_showStarsReview(isBack = false) {
		this._removeActiveEl();
		const emojisConfig = this._getEmojisConfig();

		this._dom.backButton?.remove();

		// pokud se jde zpet v ramci nestrukturovaneho hodnoceni, je treba pregenerovat hvezdickovou obrazovku
		if (!emojisConfig.length && isBack) {
			const titleCont = this._getUserTitle();
			const middleCont = node("div", { className: "middle-cont" });
			const cont = this._dom.starsReview;
			const isSmallViewport = window.innerWidth < 500;
			let starsChanged = false;
			let starsPicker = null;
			const bottomPartData = this._buildBottomPart({
				onSave: () => {
					this._saveFromStars(this._starsPicker.percent);

					this._log("reviews_edit_stars", {
						starsEdit: starsChanged ? 1 : 0,
					});
				},
				saveTitle: _("continue"),
			});

			cont.innerHTML = "";

			starsPicker = new FirmReviewStarsPicker({
				starWidth: isSmallViewport ? 40 : 48,
				starOffset: isSmallViewport ? 10 : 13,
				noTitle: true,
				clickCb: starsData => {
					// analytika hvezd
					const starsCount = ((starsData.stars - 1) * 2) + (starsData.full ? 2 : 1);

					starsChanged = true;

					this._log("click", {
						text: `stars-updated-${starsCount}`,
					});
				},
			});

			starsPicker.percent = this._myReview.rating;

			middleCont.append(
				starsPicker.container,
			);
			cont.append(
				titleCont,
				middleCont,
				bottomPartData.container,
			);
			cont.classList.add("variant-a");
			this._starsPicker = starsPicker;

			Object.assign(this._dom, {
				saveBtn: bottomPartData.saveBtn,
				errorInfo: bottomPartData.errorInfo,
			});

			this._logOrigin = "step1Edit";
		} else {
			this._logOrigin = "step0";
		}

		this._activeEl = this._dom.starsReview;
		this._dom.container.appendChild(this._activeEl);
	}

	_showTextReview() {
		this._removeActiveEl();

		this._activeEl = this._dom.textReview;
		if (this._dom.textReview.children.length) {
			const forceStickyMode = window.matchMedia(SMALL_MEDIA_QUERY).matches;

			this._dom.textReview.firstElementChild.replaceWith(this._buildReviewLine(forceStickyMode));
		} else {
			this._buildTextReview();
		}
		this._dom.container.appendChild(this._activeEl);
		this._dialog.dom.container.prepend(this._dom.backButton);
		this._dom.text.focus();
		this._logOrigin = "step2";
	}

	_getUserTitle() {
		const titleCont = node("div", { className: "title-cont" });
		const userName = `<strong>${Mapy.Util.escapeTags(getUserName())}</strong>`;
		const userDesc = node("p", { className: "user", innerHTML: _("detail.content.review2.reviewAsUser", { user: userName }) });
		const { user } = store.getState().user;

		// overeny uzivatel
		if (user.bankId) {
			const verifiedIcon = icon("verifieduser16");

			userDesc.appendChild(verifiedIcon);
			createVerifiedUserTooltip(verifiedIcon);
		}

		titleCont.append(
			node("h3", { className: "title", textContent: this._opts.data.title }),
			userDesc,
		);

		return titleCont;
	}

	_createStarsReview() {
		const isSmallViewport = window.innerWidth < 500;

		return new FirmStarsReview({
			data: this._opts.data,
			logSource: this._opts.logSource,
			logPosition: this._opts.logPosition,
			logRecenzeHvezdy: false,
			starWidth: isSmallViewport ? 40 : 48,
			starOffset: isSmallViewport ? 10 : 13,
			fromOtherPOI: this._opts.fromOtherPOI,
			loaderStart: () => {
				this._createLoader();
			},
			loaderEnd: () => {
				this._removeLoader();
			},
			showPopupCb: putReviewData => {
				this._showThankYou = true;
				this._detailReview = putReviewData.review;
				this._myReview = putReviewData.myReview;
				this._logRecenzeLeti = true;

				this._showTextReview();
			},
			onError: () => {
				this._logOrigin = "step1Failed";
				this._log("reviews_fail_stars");
			},
			logObj: this._opts.logObj,
		});
	}

	_buildBottomPart({
		onSave = () => { /**/ },
		saveTitle = _("detail.content.review2.addReview"),
	}) {
		const bottomPart = node("div", { className: "bottom-part" });
		const controlPart = node("div", { className: "control-part" });
		const lang = CONFIG.defaultLang;
		const rulesSource = this._opts.data.isFirm ? RULES_LINKS : RULES_LINKS_NOFIRM;
		const rulesLink = Object.keys(rulesSource).indexOf(lang) > -1 ? rulesSource[lang] : rulesSource.en;
		const termsLink = `https://licence.mapy.${CONFIG.TLD}/?doc=mapy_lu&lang=${lang}`;
		const agreement = node("p", {
			className: "licence",
			innerHTML: _("detail.content.review2.commentLicence", {
				linkAgrStart: `<a href="${termsLink}" target="_blank">`,
				linkAgrEnd: "</a>",
				linkRulStart: `<a href="${rulesLink}" target="_blank">`,
				linkRulEnd: "</a>",
			}),
		});
		const linksEl = Array.from(agreement.querySelectorAll("a"));

		linksEl.forEach((item, ind) => {
			item.setAttribute("data-log-ignore", "true");
			item.addEventListener("pointerdown", () => {
				this._log("click", {
					url: item.href,
					text: ind === 0 ? "podmínkami" : "pravidly",
				});
			});
		});

		const buttons = node("div", { className: "buttons" });
		const saveBtn = node("button", { className: "save-btn" });

		saveBtn.textContent = saveTitle;
		saveBtn.setAttribute("data-log-ignore", "");
		saveBtn.addEventListener("click", event => {
			event.stopPropagation();
			event.preventDefault();
			onSave && onSave();
		});

		buttons.appendChild(saveBtn);
		const loaderImg = node("img", { src: "/img/loader.gif", className: "save-loader" });

		buttons.appendChild(loaderImg);
		controlPart.appendChild(agreement);
		controlPart.appendChild(buttons);

		const errorInfo = node("p", { className: "error" });

		bottomPart.appendChild(controlPart);
		bottomPart.appendChild(errorInfo);

		return {
			container: bottomPart,
			saveBtn,
			errorInfo,
		};
	}

	_buildReviewLine(forceStickyMode: boolean) {
		// hodnoceni
		const reviewLine = node("div", { className: "review-line" });
		// stars count
		const starsBanner = new StarsBanner({
			percentage: this._myReview.rating,
		});

		forceStickyMode && this._dialog.dom.container.classList.add("sticky-mode");

		reviewLine.appendChild(starsBanner.container);

		return reviewLine;
	}

	_buildTextReview() {
		if (!this._myReview) {
			return;
		}

		const forceStickyMode = window.matchMedia(SMALL_MEDIA_QUERY).matches;

		this._dom.textReview.appendChild(this._buildReviewLine(forceStickyMode));
		this._dom.textReview.appendChild(node("p", { className: "thank-comment", textContent: _("detail.content.review2.motivateToComment") }));
		// text
		const text = node("textarea", { className: "text", placeholder: _("detail.content.review2.writePlaceholder2"), value: this._opts.initialText });

		text.setAttribute("maxlength", MAX_LEN.toString());

		text.addEventListener("input", () => {
			this._updateRest(true);
		});

		const counter = node("p", { className: "counter" });

		this._dom.textReview.appendChild(text);
		this._dom.textReview.appendChild(counter);

		this._reviewPhotosUpload = createReviewPhotosUpload({
			fivePerRow: true,
			onUpdate: data => {
				this._dom.errorInfo.textContent = "";

				if (!forceStickyMode) {
					if (data.count > 4) {
						this._dialog.dom.container.classList.add("sticky-mode");
					} else {
						this._dialog.dom.container.classList.remove("sticky-mode");
					}
				}

				if (data.loading) {
					this._dom.container.classList.add("loading");
				} else {
					this._dom.container.classList.remove("loading");
				}

				this._updateRest(true);

				if (data.type === "add-files") {
					if (!this._addPhotoWasSended) {
						this._log("click", {
							text: "photoAdd",
						});
						this._addPhotoWasSended = true;
					}
				}
			},
			onError: data => {
				this._dom.errorInfo.textContent = data.msg;
			},
		});
		this._dom.textReview.append(this._reviewPhotosUpload.container);

		if (this._opts.initialImages.length) {
			this._reviewPhotosUpload.addPhotos(this._opts.initialImages, true);
		}

		if (forceStickyMode) {
			this._dom.textReview.append(node("div", { style: "height: 16px" }));
		}

		const bottomPartData = this._buildBottomPart({
			onSave: () => {
				this._save();
			},
		});

		this._dom.textReview.appendChild(bottomPartData.container);

		const backButton = node("button", { className: "reviewpopup__back-button" });

		backButton.appendChild(createSDSIcon({
			symbol: ARROW_LEFT_24,
		}));
		backButton.setAttribute("data-log-ignore", "true");
		backButton.addEventListener("click", event => {
			event.stopPropagation();
			const emojisConfig = this._getEmojisConfig();

			Mapy.getComponent("logger").log("click", {
				text: "backToStars",
				position: this._opts.logPosition,
				rating_layout: getFirmLayout(this._opts.data.review, this._opts.data.booking, null, this._opts.data.isFirm),
				sourcePoi: this._opts.data.source,
				typeid: this._opts.data.typeId,
				firmTwin: this._opts.data.isTwin ? 1 : 0,
				source: this._opts.logSource,
				section: "reviews",
				type: "recenze-edit",
				...this._opts.logObj,
			});

			this._starsUpdated = false;
			this._emojisUpdated = false;

			// u strukturka jdeme opravdu zpet v tomhle dialogu
			if (emojisConfig.length || !this._opts.onBack) {
				this._showStarsReview(true);
				this._logOrigin = "attributesDialogue";
			} else {
				this._opts.onBack && this._opts.onBack({
					...this._myReview,
					text: text.value,
				}, this._reviewPhotosUpload.getIImages());
			}
		});

		Object.assign(this._dom, {
			text,
			counter,
			saveBtn: bottomPartData.saveBtn,
			errorInfo: bottomPartData.errorInfo,
			backButton,
		});

		this._updateRest();
	}

	_updateRest(logAnalytics?: boolean) {
		const IS_EMPTY_CLASS = "is-empty";
		const hasPhotos = this._reviewPhotosUpload ? !this._reviewPhotosUpload.isEmpty() : false;
		const chars = MAX_LEN - this._dom.text.value.length;

		this._dom.counter.innerHTML = _("detail.content.review.numOfChar", {
			chars,
		});

		if (this._dom.text.value.length || hasPhotos) {
			this._dom.saveBtn.classList.remove(IS_EMPTY_CLASS);
			this._dom.saveBtn.textContent = _("detail.content.review2.addReview");
			this._logOrigin = "step3";
		} else {
			this._dom.saveBtn.classList.add(IS_EMPTY_CLASS);
			this._dom.saveBtn.textContent = _("detail.content.review2.addNothing");
			this._logOrigin = "step2";
		}

		if (logAnalytics && !this._keyInputAnalytics && this._dom.text.value.length > 0) {
			// analytika
			this._log("text_fill");
			this._keyInputAnalytics = true;
		}
	}

	_save() {
		const text = this._dom.text.value.trim();
		const mediaIds = this._reviewPhotosUpload.getMediaIds();

		if (text || mediaIds.length) {
			this._dom.errorInfo.textContent = "";
			this._dom.container.classList.add("loading");

			this._createLoader();

			// analytika - jiz nechci nic dodat
			if (text) {
				this._log("click", {
					text: "Přidat komentář",
				});
			} else {
				this._log("click", {
					text: "Již nechci nic dodat",
				});
			}

			putReview({
				source: this._opts.data.source,
				...this._opts.logSource === FULLTEXT_REVIEW
					? {
						reviewSource: FULLTEXT_REVIEW_SOURCE,
					}
					: {},
				poiId: this._opts.data.id,
				text,
				mediaIds,
				structuredRating: getStructuredRating(this._myReview),
				rating: this._myReview.rating,
				activeRequest: this._activeRequest,
			}).then(putReviewData => {
				// nova analytika
				this._logRecenzeLeti = false;
				this._log("recenze-leti", {
					type: text ? "recenze-text" : "recenze-stars",
					photoCount: mediaIds.length,
					...this._emojis ? parseStructuredRating(this._emojis.getData()) : {},
				});
				this._detailReview && Object.assign(this._detailReview, putReviewData.review || {});
				this._myReview = putReviewData.myReview;
				this._showThankYou = true;
				// ulozime do kese
				putToCacheWithoutDetail(putReviewData, this._opts.data.source, this._opts.data.id);
				this._removeLoader();
				// nacteme zrovna data pro detail
				this._closeDialog();
			}, () => {
				this._logOrigin = "step3Failed";
				// analytika
				this._log("reviews_fail_text");
				this._removeLoader();
				this._showSaveError();
			});
		} else {
			// analytika - jiz nechci nic dodat
			this._log("click", {
				text: "Již nechci nic dodat",
			});
			this._log("reviews_form_close_button", {
				origin: "step2",
			});
			this._showThankYou = true;
			this._closeDialog();
		}
	}

	_saveFromStars(percent: number, structuredRating: TStructuredData = []) {
		const removeLoading = () => {
			this._removeLoader();
			this._dom.container.classList.remove("loading");
		};

		this._createLoader();
		this._dom.container.classList.add("loading");

		// ulozime
		loginWithDetail({
			source: this._opts.data.source,
			id: this._opts.data.id,
		}).then(data => {
			// analytika hvezd
			const loginOutput = {
				isOwner: data.isOwner,
				myReview: data.myReview,
			};

			if (loginOutput.isOwner) {
				removeLoading();
				this._dom.errorInfo.textContent = _("detail.content.review2.putReviewError");

				return;
			}

			putReview({
				source: this._opts.data.source,
				...this._opts.logSource === FULLTEXT_REVIEW
					? {
						reviewSource: FULLTEXT_REVIEW_SOURCE,
					}
					: {},
				poiId: this._opts.data.id,
				text: "",
				mediaIds: [],
				structuredRating,
				rating: percent,
				activeRequest: this._activeRequest,
			}).then(putReviewData => {
				removeLoading();

				// analytika
				if (structuredRating.length) {
					this._log("attributes_set", parseStructuredRating(structuredRating, true));
				}
				this._detailReview = {
					...data.review,
					...putReviewData.review,
				};
				this._myReview = putReviewData.myReview;

				// ulozime do kese
				putToCacheWithoutDetail(putReviewData, this._opts.data.source, this._opts.data.id);

				// analytika
				this._logRecenzeLeti = true;

				this._showTextReview();
			}, () => {
				removeLoading();
				this._dom.errorInfo.textContent = _("detail.content.review2.putReviewError");
			});
		}, () => {
			removeLoading();
			this._dom.errorInfo.textContent = _("detail.content.review2.putReviewError");
		});
	}

	_showSaveError() {
		this._dom.container.classList.remove("loading");
		this._dom.errorInfo.textContent = _("detail.content.review2.saveErrorComment");
	}

	_dialogOnClose() {
		if (this._locked) {
			return;
		}

		// neni hodnoceni - skip
		if (!this._myReview) {
			let logCloseAnalytics = true;

			if (this._getEmojisConfig().length > 0) {
				// vlastni analytika pro zavreni
				this._log("reviews_form_close", {
					origin: "attributesDialogue",
				});
				logCloseAnalytics = false;
			}

			this._closeDialog(logCloseAnalytics);

			return;
		}

		// vytvorime
		const closeOverlay = node("div", { className: "close-overlay" });
		const closeHeader = node("div", { className: "close-header" });
		const closePrompt = node("div", { className: "close-prompt content-feedback" });
		const text = node("p", { textContent: _("detail.content.review2.deleteComment") });
		const btnThrowAway = node("button", { type: "button", className: "throw-away" });
		const btnThrowAwayText = node("span", { textContent: _("delete"), className: "text" });

		btnThrowAway.appendChild(btnThrowAwayText);
		btnThrowAway.appendChild(node("img", { src: "/img/loader.gif", className: "delete-loader" }));
		btnThrowAway.setAttribute("data-log-ignore", "true");
		btnThrowAway.addEventListener("click", () => {
			this._locked = false;
			this._logOrigin += "Delete";
			this._log("click", {
				text: "Smazat",
			});
			// reset
			this._dom.container.classList.add("loading-delete");

			// mame co mazat?
			if (this._myReview) {
				this._createLoader(true);
				// odstranime hodnoceni
				const reasonsList = REMOVE_REASONS_LIST;

				deleteReview({
					reviewId: this._myReview.id,
					...this._opts.data.isFirm
						? {
							reason: reasonsList[reasonsList.length - 1].id,
							text: "user_revert",
						}
						: {
							reason: 0,
							text: "",
						},
				}).then(updatedReview => {
					// aktualizace poctu hodnoceni
					this._detailReview = {
						...this._detailReview,
						...Object.keys(updatedReview).length > 0
							? updatedReview
							: {
								total: this._detailReview.total - 1,
							},
					};

					this._myReview = null;
					this._logRecenzeLeti = false;
					this._wasRemoved = true;

					this._removeLoader();
					this._showThankYou = false;
					this._closeDialog(true);
					createDeleteInfo();
				}, () => {
					this._removeLoader();
					this._dom.container.classList.remove("loading-delete");
					text.textContent = _("detail.content.review2.removeError");
					btnThrowAwayText.classList.add("try-again");
					btnThrowAwayText.textContent = _("tryAgain");
				});
			} else {
				this._myReview = null;
				this._logRecenzeLeti = false;
				this._wasRemoved = true;

				this._showThankYou = false;

				if (this._getEmojisConfig().length === 0) {
					this._closeDialog(true);
				} else {
					// vlastni analytika pro zavreni
					this._log("reviews_form_close", {
						origin: "attributesDialogue",
					});

					this._closeDialog();
				}
			}
		});
		const btnCancel = node("button", { type: "button", textContent: _("cancel"), className: "cancel" });

		btnCancel.setAttribute("data-log-ignore", "true");
		btnCancel.addEventListener("click", event => {
			event.stopPropagation();
			event.preventDefault();
			// analytika - jiz nechci nic dodat
			this._log("click", {
				text: "Zrušit",
			});
			closeOverlay.remove();
			this._locked = false;
		});
		closePrompt.appendChild(text);
		closePrompt.appendChild(btnThrowAway);
		closePrompt.appendChild(btnCancel);
		closeOverlay.appendChild(closeHeader);
		closeHeader.appendChild(closePrompt);
		this._dom.container.appendChild(closeOverlay);
		this._locked = true;
	}

	_closeDialog(logFormClose?: boolean) {
		// analytika pro zavreni
		if (logFormClose) {
			this._log("reviews_form_close", {
				origin: this._logOrigin,
			});
		}

		if (this._logRecenzeLeti) {
			this._log("recenze-leti", {
				type: "recenze-stars",
				photoCount: 0,
				...this._emojis ? parseStructuredRating(this._emojis.getData()) : {},
			});

			this._logRecenzeLeti = false;
		}

		if (this._myReview) {
			sklikReviewSend();
		}

		document.body.style.overflow = "";
		this._dialog.close();
		// dialog se zavrel, zavolame vlastni signal
		this._opts.onClose && this._opts.onClose({
			review: this._detailReview,
			myReview: this._myReview,
			wasRemoved: this._wasRemoved,
		});

		// zobrazime srdicko?
		if (this._showThankYou && this._opts.showThankYou) {
			const hearthEl = node("div", { className: "firm-review-popup-hearth" });

			hearthEl.appendChild(icon("check-circle"));
			hearthEl.appendChild(node("h3", { textContent: _("detail.content.review2.thankYouResponse") }));
			document.body.appendChild(hearthEl);

			setTimeout(() => {
				hearthEl.remove();
			}, REVIEW_THANK_YOU_TO);
		}
	}

	_removeActiveEl() {
		if (this._activeEl) {
			this._activeEl.remove();
			this._activeEl = null;
		}
	}

	_createLoader(isTextReview?: boolean) {
		this._locked = true;

		if (!this._dom.loaderCover) {
			this._dom.loaderCover = node("div", { className: "loader-cover" });
			this._dom.container.appendChild(this._dom.loaderCover);
		}

		if (!this._dom.loaderImg && !isTextReview) {
			const loaderImg = node("img", { src: "/img/loader.gif" });

			this._starsReview?.responseEl.appendChild(loaderImg);
			this._dom.loaderImg = loaderImg;
		}
	}

	_removeLoader() {
		this._locked = false;

		if (this._dom.loaderCover) {
			this._dom.loaderCover.remove();
			this._dom.loaderCover = null;
		}

		if (this._dom.loaderImg) {
			this._dom.loaderImg.remove();
			this._dom.loaderImg = null;
		}
	}

	_log(name: string, data?: object) {
		/* eslint-disable camelcase */
		Mapy.getComponent("logger").log(name, {
			position: this._opts.logPosition,
			rating_layout: getFirmLayout(this._opts.data.review, this._opts.data.booking, null, this._opts.data.isFirm),
			sourcePoi: this._opts.data.source,
			typeid: this._opts.data.typeId,
			firmTwin: this._opts.data.isTwin ? 1 : 0,
			source: this._opts.logSource,
			section: "reviews",
			type: "recenze",
			...this._opts.logObj,
			...data,
		});
	}

	_getEmojisConfig() {
		const items = this._opts.data.structuredRating || [];

		// zrusime rating -> nechceme pri zadavani recenze
		return items.map(item => ({
			...item,
			rating: null,
		}));
	}
}
